import { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStock, setFilters, resetFilters } from '../../features/inventory/stockListSlice';
import Styles from '../../assets/css/components/inventory/stockList.module.scss';
import CompanyDetailsModal from '../../components/inventory/CompanyDetailsModal';
import { AiOutlineSearch } from "react-icons/ai";
import { RiFilter2Line } from "react-icons/ri";
import { IoMdClose } from "react-icons/io";
import { BsThreeDotsVertical } from "react-icons/bs";
import { PiBuildingOffice } from "react-icons/pi";
import Pagination from '../../components/common/Pagination';
import Loader from '../../utils/Loader';

const StockList = () => {
    const dispatch = useDispatch();
    const { stocks, loading, error, pagination, filters } = useSelector((state) => state.inventoryList);
    const logisticsId = localStorage.getItem('logisticsId');

    const [isOpen, setIsOpen] = useState(false);
    const [filterDate, setFilterDate] = useState({
        from: '',
        to: ''
    });
    const [activeDropdown, setActiveDropdown] = useState(null);
    const filterDropdownRef = useRef(null);
    const filterButtonRef = useRef(null);
    const companyDropdownRef = useRef(null);
    const [dateError, setDateError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [showCompanyModal, setShowCompanyModal] = useState(false);

    const sortedStocks = useMemo(() => {
        if (!stocks.length) return [];
        const stocksCopy = [...stocks];

        return stocksCopy.sort((a, b) => {
            if (a.product_availability === 'low' && b.product_availability !== 'low') {
                return -1;
            }
            if (b.product_availability === 'low' && a.product_availability !== 'low') {
                return 1;
            }
            return 0;
        });
    }, [stocks]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Reset filters on component mount
    useEffect(() => {
        dispatch(resetFilters());
    }, [dispatch]);

    // Fetch data when filters change
    useEffect(() => {
        if (logisticsId) {
            dispatch(fetchStock(logisticsId));
        }
    }, [dispatch, logisticsId, filters]);

    // Update local state when filters change
    useEffect(() => {
        if (filters.search !== searchTerm) {
            setSearchTerm(filters.search || '');
        }

        if (filters.from !== filterDate.from || filters.to !== filterDate.to) {
            setFilterDate({
                from: filters.from || '',
                to: filters.to || ''
            });
        }
    }, [filters]);

    const handleProductCompanyDetails = (product) => {
        if (product.company) {
            setSelectedCompany(product.company);
            setShowCompanyModal(true);
            setActiveDropdown(null);
        }
    };

    const closeCompanyModal = () => {
        setShowCompanyModal(false);
        setSelectedCompany(null);
    };

    const dateStatusApply = () => {
        if ((filterDate.from && !filterDate.to) || (!filterDate.from && filterDate.to)) {
            setDateError("Please select both From and To dates");
            return;
        }

        if (filterDate.from && filterDate.to && new Date(filterDate.from) > new Date(filterDate.to)) {
            setDateError("From date cannot be after To date");
            return;
        }

        const dateFilters = {
            from: filterDate.from,
            to: filterDate.to,
            page: "1"
        };

        dispatch(setFilters(dateFilters));
        setIsOpen(false);
        setDateError('');
    };

    const handleClear = () => {
        setFilterDate({ from: '', to: '' });
        setDateError('');
    };

    const handleReset = () => {
        setFilterDate({ from: '', to: '' });
        setSearchTerm('');
        setDateError('');
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (window.searchTimeout) {
            clearTimeout(window.searchTimeout);
        }

        window.searchTimeout = setTimeout(() => {
            dispatch(setFilters({
                search: value,
                page: "1"
            }));
        }, 500);
    };

    const handleDateChange = (type, value) => {
        setFilterDate(prev => ({
            ...prev,
            [type]: value
        }));
        setDateError('');
    };

    const changePage = (count, type) => {
        if (type === "page") {
            dispatch(setFilters({
                ...filters,
                page: count.toString()
            }));
        }
        if (type === "show") {
            dispatch(setFilters({
                ...filters,
                page: "1",
                paginate_per_page: count.toString()
            }));
        }
    };

    const handleDropDown = (productId, event) => {
        event.stopPropagation();
        setActiveDropdown(activeDropdown === productId ? null : productId);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '/');
    };

    const truncateText = (text, maxLength = 20) => {
        if (!text) return '';
        if (isMobile && text.length > maxLength) {
            return text.substring(0, maxLength) + '...';
        }
        return text;
    };

    // Handle click outside for dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Close filter dropdown
            if (
                isOpen &&
                filterDropdownRef.current &&
                !filterDropdownRef.current.contains(event.target) &&
                !filterButtonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }

            // Close company dropdown
            if (
                activeDropdown &&
                companyDropdownRef.current &&
                !companyDropdownRef.current.contains(event.target)
            ) {
                setActiveDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, activeDropdown]);

    if (!logisticsId) {
        return (
            <div className={`${Styles.stock_container}`}>
                <div className={`${Styles.stock_heading}`}>
                    <h2>Product List</h2>
                </div>
                <div className={`${Styles.no_data}`}>
                    Please login to view products
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={`${Styles.stock_container}`}>
                <div className={`${Styles.stock_heading}`}>
                    <h2>Stock List</h2>
                </div>

                <div className={`${Styles.stock_list}`}>
                    {/* Filter And Search Data */}
                    <div className={`${Styles.filter_search_btn}`}>
                        <div className={`${Styles.filter}`}>
                            <div className={`${Styles.sub_filter}`}>
                                <button
                                    ref={filterButtonRef}
                                    onClick={() => setIsOpen(!isOpen)}
                                    aria-expanded={isOpen}
                                    className={isOpen ? Styles.active : ''}
                                >
                                    <RiFilter2Line /> {isMobile ? '' : 'Filter'}
                                </button>
                                {isOpen && (
                                    <div className={`${Styles.filter_dropdown}`} ref={filterDropdownRef}>
                                        <div className={`${Styles.heading}`}>
                                            <p>Filter</p>
                                            <IoMdClose onClick={() => setIsOpen(false)} />
                                        </div>

                                        <div className={`${Styles.selectDate}`}>
                                            <div className={`${Styles.heading_clear}`}>
                                                <p>Select Date Range</p>
                                                <p onClick={handleClear}>Clear</p>
                                            </div>
                                            <div className={`${Styles.filter_date}`}>
                                                <div className={`${Styles.from}`}>
                                                    <label htmlFor="from">From:</label>
                                                    <input
                                                        type="date"
                                                        id='from'
                                                        placeholder='DD-MM-YYYY'
                                                        value={filterDate.from}
                                                        max={filterDate.to || undefined}
                                                        onChange={(e) => handleDateChange('from', e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <div className={`${Styles.to}`}>
                                                    <label htmlFor="to">To:</label>
                                                    <input
                                                        type="date"
                                                        id='to'
                                                        placeholder='DD-MM-YYYY'
                                                        value={filterDate.to}
                                                        min={filterDate.from || undefined}
                                                        onChange={(e) => handleDateChange('to', e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                            </div>
                                            {dateError && <div className={`${Styles.date_error}`}>{dateError}</div>}
                                        </div>

                                        <div className={`${Styles.buttons}`}>
                                            <button onClick={handleReset}>Reset</button>
                                            <button onClick={dateStatusApply}>Apply Now</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={`${Styles.search}`}>
                            <div className={`${Styles.inputContainer}`}>
                                <AiOutlineSearch className={`${Styles.searchIcon}`} />
                                <input
                                    type="text"
                                    placeholder="Search by Product Name"
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Product Table */}
                    <div className={`${Styles.stock_table_container}`}>
                        <div className={`${Styles.item_table}`}>
                            {loading ? (
                                <div className={`${Styles.loader_container}`}>
                                    <Loader size="large" color="primary" />
                                </div>
                            ) : error ? (
                                <div className={Styles.error_message}>
                                    {error}
                                </div>
                            ) : sortedStocks.length === 0 ? (
                                <div className={`${Styles.no_data}`}>
                                    No Products found
                                </div>
                            ) : (
                                <table>
                                    <thead>
                                        <tr>
                                            <th className={`${Styles.first_row}`}>No.</th>
                                            <th>Product Name</th>
                                            <th>Brand</th>
                                            <th>SKU</th>
                                            <th>Quantity</th>
                                            {/* <th>Minimum Stock</th> */}
                                            <th>Status</th>
                                            {/* <th>Expiry Date</th> */}
                                            {!isMobile && <th>Created Date</th>}
                                            <th aria-label="Actions"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedStocks.map((product, index) => {
                                            const isLowStock = product.product_availability === 'low';
                                            return (
                                                <tr
                                                    key={product.id || index}
                                                    className={`${isLowStock ? Styles.lowStockRow : ''}`}
                                                >
                                                    <td className={`${Styles.first_row}`}>
                                                        {(pagination.currentPage - 1) * pagination.showing + index + 1}
                                                    </td>
                                                    <td>
                                                        <div className={`${Styles.item_name}`}>
                                                            <span className={`${Styles.stock_name_text}`}>
                                                                {truncateText(product.product_name, isMobile ? 15 : 30)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {truncateText(product.brand_name, isMobile ? 15 : 30)}
                                                    </td>
                                                    <td>
                                                        {truncateText(product.sku_number, isMobile ? 10 : 20)}
                                                    </td>
                                                    <td className={`${Styles.quantity_cell} ${isLowStock ? Styles.lowStockQuantity : ''}`}>
                                                        <span className={`${Styles.quantity_badge}`}>
                                                            {product.quantity}
                                                        </span>
                                                    </td>
                                                    {/* <td className={`${Styles.quantity_cell} ${isLowStock ? Styles.lowStockQuantity : ''}`}>
                                                        <span className={`${Styles.quantity_badge}`}>
                                                            {product.company_warehouse.min_lot}
                                                        </span>
                                                    </td> */}
                                                    <td className={`${Styles.status_badge} ${product.status === 1 ? Styles.active : Styles.inactive} ${isLowStock ? Styles.lowStockStatus : ''}`}>
                                                        {product.status === 1 ? 'Active' : 'Inactive'}
                                                    </td>
                                                    {/* <td className={`${isLowStock ? Styles.lowStockExpiry : ''}`}>
                                                    {product.expire_date ? formatDate(product.expire_date) : 'N/A'}
                                                </td> */}
                                                    {!isMobile && <td className={`${isLowStock ? Styles.lowStockCreated : ''}`}>{formatDate(product.created_at)}</td>}
                                                    <td>
                                                        <div className={`${Styles.manage}`}>
                                                            <BsThreeDotsVertical
                                                                onClick={(e) => { e.stopPropagation(); handleDropDown(product.id, e) }}
                                                                aria-label="Actions"
                                                            />
                                                            {activeDropdown === product.id && (
                                                                <div className={`${Styles.manageUser} ${isMobile ? Styles.mobile_dropdown : ''}`} ref={companyDropdownRef}>
                                                                    <p onClick={() => handleProductCompanyDetails(product)}>
                                                                        <span><PiBuildingOffice /></span>
                                                                        <span>Company</span>
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    {/* Pagination */}
                    {sortedStocks && sortedStocks.length > 0 && (
                        <Pagination data={pagination} pageClick={changePage} />
                    )}
                </div>
            </div>

            {/* Company Details Modal */}
            {showCompanyModal && (
                <CompanyDetailsModal
                    company={selectedCompany}
                    onClose={closeCompanyModal}
                />
            )}
        </>
    )
}

export default StockList;